import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Sparkles, RotateCcw, AlertTriangle,
  CheckCircle2, XCircle, Copy, ShieldCheck, ShieldX, Download, Save,
  Circle, Check,
} from "lucide-react";
import { useProgressSteps } from "../hooks/useProgressSteps";
import { ProgressSteps } from "../components/ui/ProgressSteps";
import ResumeTemplatePicker from "../components/resume/ResumeTemplatePicker";
import UpgradeModal from "../components/UpgradeModal";

const ANALYZE_STEPS = [
  { label: "Parsing resume", duration: 3500 },
  { label: "Extracting skills", duration: 4000 },
  { label: "Matching JD keywords", duration: 4000 },
  { label: "Scoring ATS compatibility", duration: 0 },
];

const OPTIMIZE_STEPS = [
  { label: "Parsing resume", duration: 3000 },
  { label: "Scoring keyword density", duration: 4000 },
  { label: "Rewriting sections", duration: 8000 },
  { label: "Final ATS check", duration: 0 },
];

const MAX_ANALYSES = 5;
const MAX_RESUMES = 5;

// ─── helpers ───────────────────────────────────────────────
function atsColor(v) { return v < 40 ? "#E11D48" : v < 70 ? "#F59E0B" : "#10B981"; }

function ScoreRing({ value, label, color }) {
  const v = Math.round(value || 0);
  const c = color || atsColor(v);
  const data = [{ value: v, fill: c }];
  return (
    <div className="flex flex-col items-center">
      <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em] mb-3">{label}</div>
      <div className="relative h-40 w-40">
        <ResponsiveContainer>
          <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background dataKey="value" cornerRadius={0} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-4xl font-black" style={{ color: c }}>{v}</div>
          <div className="font-mono text-[10px] text-muted-foreground">/ 100</div>
        </div>
      </div>
    </div>
  );
}

function DownloadButton({ resumeId, variant, label }) {
  const download = async (fmt) => {
    try {
      const res = await api.get(`/resume/saved/${resumeId}/download`, {
        params: { variant, fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resume_${variant}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Download failed"); }
  };
  return (
    <div className="flex gap-1">
      <button
        onClick={() => download("pdf")}
        className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-xl border border-border hover:bg-secondary/60 transition-colors"
        title={`Download ${label} as PDF`}
      >
        <Download className="w-3 h-3" /> PDF
      </button>
      <button
        onClick={() => download("docx")}
        className="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-xl border border-border hover:bg-secondary/60 transition-colors"
        title={`Download ${label} as DOCX`}
      >
        <Download className="w-3 h-3" /> DOCX
      </button>
    </div>
  );
}

// ─── save optimized panel ──────────────────────────────────
function SaveOptimizedPanel({ optimized, savedResumes, resumesAtLimit, savingOptimized, onSaveTo, onSaveBoth }) {
  const [newName, setNewName] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);

  return (
    <div className="rounded-xl border border-border p-4 bg-white mb-3">
      <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em] mb-3">SAVE ATS OPTIMISED RESUME</div>

      {/* Attach to an existing saved resume */}
      {savedResumes.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-2">Attach to an existing resume:</div>
          <div className="flex flex-wrap gap-2">
            {savedResumes.map((r) => (
              <Button key={r.resume_id} variant="outline" className="rounded-xl h-8 text-xs" disabled={savingOptimized}
                onClick={() => onSaveTo(r.resume_id)}>
                <Save className="w-3 h-3 mr-1" /> {r.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Save as new resume — always available unless at limit */}
      {resumesAtLimit ? (
        <div className="flex items-center gap-2 text-xs text-[#92400E]">
          <AlertTriangle className="w-3 h-3 text-[#F59E0B]" />
          Resume limit reached (5/5). Delete a resume to save this one.
        </div>
      ) : (
        <div>
          <div className="text-xs text-muted-foreground mb-2">
            {savedResumes.length > 0 ? "Or save as a new resume:" : "Save as a new resume:"}
          </div>
          {!showNewForm ? (
            <button onClick={() => setShowNewForm(true)}
              className="flex items-center gap-1 text-xs font-mono text-[#0025D8] hover:underline">
              <Save className="w-3 h-3" /> Save as new resume →
            </button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                className="rounded-xl border border-border px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#0025D8] w-56"
                placeholder="Resume name…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSaveBoth(newName)}
                autoFocus
              />
              <Button onClick={() => onSaveBoth(newName)} disabled={savingOptimized || !newName.trim()}
                className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] h-8 text-xs">
                {savingOptimized ? "Saving…" : "Save"}
              </Button>
              <button onClick={() => { setShowNewForm(false); setNewName(""); }}
                className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── input panel ───────────────────────────────────────────
function InputPanel({ label, textValue, onTextChange, file, onFile, onClearFile, mode, onModeChange, savedResumes, onSelectSaved }) {
  const fileRef = useRef();
  return (
    <div className="rounded-xl border border-border bg-white">
      <div className="flex items-center justify-between border-b border-border px-4 py-3 flex-wrap gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => onModeChange("text")}
            className={`px-3 py-1 rounded-lg text-xs font-mono border transition-colors ${mode === "text" ? "bg-[#0025D8] text-white border-[#0025D8]" : "border-border text-muted-foreground hover:text-foreground"}`}>
            Paste text
          </button>
          <button onClick={() => onModeChange("file")}
            className={`px-3 py-1 rounded-lg text-xs font-mono border transition-colors ${mode === "file" ? "bg-[#0025D8] text-white border-[#0025D8]" : "border-border text-muted-foreground hover:text-foreground"}`}>
            Upload file
          </button>
          {label.includes("Resume") && savedResumes?.length > 0 && (
            <button onClick={() => onModeChange("saved")}
              className={`px-3 py-1 rounded-lg text-xs font-mono border transition-colors ${mode === "saved" ? "bg-[#0025D8] text-white border-[#0025D8]" : "border-border text-muted-foreground hover:text-foreground"}`}>
              Saved
            </button>
          )}
        </div>
      </div>
      <div className="p-4">
        {mode === "text" && (
          <textarea
            className="w-full rounded-xl border border-border p-3 text-sm bg-white min-h-[220px] resize-none focus:outline-none focus:ring-1 focus:ring-[#0025D8] font-mono"
            placeholder={label.includes("Resume") ? "Paste your resume here…" : "Paste the job description here…"}
            value={textValue}
            onChange={(e) => onTextChange(e.target.value)}
          />
        )}
        {mode === "file" && (
          <div>
            {file ? (
              <div className="flex items-center gap-3 rounded-xl border border-border p-4 bg-secondary/30">
                <FileText className="w-5 h-5 text-[#0025D8] flex-shrink-0" />
                <span className="text-sm font-mono flex-1 truncate">{file.name}</span>
                <button onClick={onClearFile} className="text-muted-foreground hover:text-[#E11D48]"><XCircle className="w-4 h-4" /></button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()}
                className="w-full rounded-xl border-2 border-dashed border-border p-10 text-center text-muted-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-colors">
                <Upload className="w-6 h-6 mx-auto mb-2" />
                <div className="text-sm">Click to upload PDF, DOCX, or TXT</div>
              </button>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); e.target.value = ""; }} />
          </div>
        )}
        {mode === "saved" && (
          <div className="space-y-2">
            {savedResumes.map((r) => (
              <div key={r.resume_id} className="rounded-xl border border-border p-3 bg-white">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-semibold text-sm">{r.name}</span>
                  <Badge variant="outline" className="rounded-xl text-[10px] border-[#0025D8]/40 text-[#0025D8]">ORIGINAL</Badge>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onSelectSaved(r.resume_id, "original")}
                    className="px-3 py-1 rounded-lg text-xs font-mono border border-[#0025D8] text-[#0025D8] hover:bg-[#0025D8] hover:text-white transition-colors">
                    Use original
                  </button>
                  {r.optimized_text && (
                    <button onClick={() => onSelectSaved(r.resume_id, "optimized")}
                      className="px-3 py-1 rounded-lg text-xs font-mono border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white transition-colors">
                      Use ATS optimised
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── main page ─────────────────────────────────────────────
export default function ResumeAnalyzer() {
  const { user } = useAuth();
  const isFree = user?.plan !== "premium";
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // inputs
  const [jdMode, setJdMode] = useState("text");
  const [jdText, setJdText] = useState("");
  const [jdFile, setJdFile] = useState(null);
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [selectedSavedVariant, setSelectedSavedVariant] = useState("original");

  const analyzeProgress = useProgressSteps(ANALYZE_STEPS);
  const optimizeProgress = useProgressSteps(OPTIMIZE_STEPS);

  // analysis
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // track which saved resume (if any) was used when running this simulation
  const [simulationSavedId, setSimulationSavedId] = useState(null);

  // optimise
  const [optimizing, setOptimizing] = useState(false);
  const [optimized, setOptimized] = useState(null);
  const [showOptimized, setShowOptimized] = useState(false);
  const [optimizedSavedToId, setOptimizedSavedToId] = useState(null);
  const [savingOptimized, setSavingOptimized] = useState(false);

  // history & resumes
  const [analyses, setAnalyses] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [atLimit, setAtLimit] = useState(false);
  const [savedResumes, setSavedResumes] = useState([]);
  const [resumesAtLimit, setResumesAtLimit] = useState(false);

  const resultsRef = useRef();

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/resume/analyses");
      setAnalyses(data.analyses);
      setAtLimit(data.count >= MAX_ANALYSES);
    } catch { /* silent */ }
    finally { setLoadingHistory(false); }
  };

  const loadSavedResumes = async () => {
    try {
      const { data } = await api.get("/resume/saved");
      setSavedResumes(data.resumes);
      setResumesAtLimit(data.count >= MAX_RESUMES);
      // Restore last selection from localStorage
      const lastId = localStorage.getItem("analyzer_last_resume_id");
      const lastVariant = localStorage.getItem("analyzer_last_resume_variant") || "original";
      if (lastId && data.resumes.some(r => r.resume_id === lastId)) {
        setSelectedSavedId(lastId);
        setSelectedSavedVariant(lastVariant);
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadHistory();
    loadSavedResumes();
  }, []);

  const handleAnalyze = async () => {
    if (!selectedSavedId) { toast.error("Select a resume from your Resumes Hub first"); return; }
    const hasJd = jdMode === "file" ? !!jdFile : jdText.trim().length > 20;
    if (!hasJd) { toast.error("Please provide a job description"); return; }

    setSubmitting(true);
    analyzeProgress.start();
    setResult(null);
    setOptimized(null);
    setShowOptimized(false);
    setOptimizedSavedToId(null);
    setSimulationSavedId(selectedSavedId);

    try {
      const fd = new FormData();
      fd.append("saved_resume_id", selectedSavedId);
      fd.append("saved_resume_variant", selectedSavedVariant);
      if (jdMode === "file" && jdFile) fd.append("jd_file", jdFile);
      else fd.append("jd_text", jdText);

      const { data } = await api.post("/resume/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.was_at_limit) toast.info("Oldest simulation replaced — you were at the 5-analysis limit.");
      await loadHistory();
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally { setSubmitting(false); analyzeProgress.finish(); }
  };

  const handleOptimize = async () => {
    if (!result) return;
    setOptimizing(true);
    optimizeProgress.start();
    try {
      const { data } = await api.post(`/resume/analyses/${result.analysis_id}/optimize`);
      setOptimized(data);
      setShowOptimized(true);
      // Auto-save: if the simulation was run from a saved resume, attach immediately
      if (simulationSavedId) {
        await api.post(`/resume/saved/${simulationSavedId}/attach-optimized-text`, {
          optimized_text: data.optimized_resume,
          resume_structured: data.resume_structured || null,
          changes_made: data.changes_made || [],
        });
        setOptimizedSavedToId(simulationSavedId);
        await loadSavedResumes();
        toast.success("ATS optimised resume saved automatically");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.plan_limit) { setUpgradeOpen(true); return; }
      toast.error(typeof detail === "string" ? detail : "Optimisation failed");
    } finally { setOptimizing(false); optimizeProgress.finish(); }
  };

  const handleSaveOptimizedTo = async (resumeId) => {
    if (!optimized) return;
    setSavingOptimized(true);
    try {
      await api.post(`/resume/saved/${resumeId}/attach-optimized-text`, {
        optimized_text: optimized.optimized_resume,
        resume_structured: optimized.resume_structured || null,
        changes_made: optimized.changes_made || [],
      });
      setOptimizedSavedToId(resumeId);
      await loadSavedResumes();
      toast.success("ATS optimised resume saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSavingOptimized(false); }
  };

  // Save optimized as new resume entry
  const handleSaveBothAsNew = async (name) => {
    if (!name.trim() || !optimized) return;
    setSavingOptimized(true);
    try {
      const { data: saved } = await api.post("/resume/saved", {
        name: name.trim(),
        original_text: optimized.optimized_resume,
        resume_structured: optimized.resume_structured || null,   // lets apply auto-fill skip re-parsing
      });
      await loadSavedResumes();
      toast.success("ATS optimised resume saved — view in Resume Hub");
      setOptimizedSavedToId(saved?.resume_id || "new");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSavingOptimized(false); }
  };

  const handleDeleteAnalysis = async (id) => {
    try {
      await api.delete(`/resume/analyses/${id}`);
      if (result?.analysis_id === id) setResult(null);
      await loadHistory();
      toast.success("Simulation deleted");
    } catch { toast.error("Could not delete"); }
  };

  const loadPastResult = (a) => {
    setResult(a);
    setSimulationSavedId(a.saved_resume_id || null);
    setOptimized(a.optimized_resume ? { optimized_resume: a.optimized_resume, resume_structured: a.resume_structured || null, changes_made: a.changes_made || [], skill_gaps: a.skill_gaps || [], post_ats_score: a.post_ats_score ?? null } : null);
    setShowOptimized(false);
    setOptimizedSavedToId(null);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleSelectSaved = (resumeId, variant) => {
    setSelectedSavedId(resumeId);
    setSelectedSavedVariant(variant);
    localStorage.setItem("analyzer_last_resume_id", resumeId);
    localStorage.setItem("analyzer_last_resume_variant", variant);
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="mb-10">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Module 3 · Resume Analyzer</div>
        <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Resume Analyser.</h1>
        <p className="text-muted-foreground mt-2 text-sm">See how your resume scores against a job description — keyword gaps, formatting check, and an AI-powered rewrite to help you stand out.</p>
      </div>

      {/* Simulation limit warning */}
      {atLimit && (
        <div className="flex items-start gap-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4 mb-6">
          <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#92400E]">You have reached the 5-analysis limit. Running a new analysis will automatically replace your oldest saved result.</p>
        </div>
      )}

      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

        {/* ── Resume column ── */}
        <div className="rounded-xl border border-border bg-white">
          <div className="border-b border-border px-4 py-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Your Resume</span>
          </div>

          <div className="p-4">
            {savedResumes.length === 0 ? (
              /* Empty state */
              <div className="border border-dashed border-border p-8 text-center mb-3">
                <FileText className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-1">No saved resumes yet.</p>
                <a href="/ai-interview-screen/resumes" className="text-xs font-mono text-[#0025D8] hover:underline">
                  Go to Resumes Hub to upload one →
                </a>
              </div>
            ) : (
              <div className="space-y-3 mb-3">
                {savedResumes.map((r) => {
                  const isActive = selectedSavedId === r.resume_id;
                  return (
                    <div key={r.resume_id} className={`border p-4 bg-white transition-colors ${isActive ? "border-[#0025D8]" : "border-border"}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="font-semibold text-sm">{r.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                        {isActive && (
                          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 flex-shrink-0">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleSelectSaved(r.resume_id, "original")}
                          className={`px-3 py-1.5 text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                            isActive && selectedSavedVariant === "original"
                              ? "bg-[#10B981] text-white border-[#10B981]"
                              : "border-border text-muted-foreground hover:border-[#10B981] hover:text-[#047857]"
                          }`}
                        >
                          {isActive && selectedSavedVariant === "original"
                            ? <Check className="w-3 h-3" />
                            : <Circle className="w-3 h-3" />}
                          Use original
                        </button>
                        {r.optimized_at && (
                          <button
                            onClick={() => handleSelectSaved(r.resume_id, "optimized")}
                            className={`px-3 py-1.5 text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                              isActive && selectedSavedVariant === "optimized"
                                ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                                : "border-border text-muted-foreground hover:border-[#7C3AED] hover:text-[#7C3AED]"
                            }`}
                          >
                            {isActive && selectedSavedVariant === "optimized"
                              ? <Check className="w-3 h-3" />
                              : <Circle className="w-3 h-3" />}
                            Use ATS version
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>

        {/* ── JD column ── */}
        <InputPanel
          label="Job Description"
          mode={jdMode}
          onModeChange={setJdMode}
          textValue={jdText}
          onTextChange={setJdText}
          file={jdFile}
          onFile={setJdFile}
          onClearFile={() => setJdFile(null)}
        />
      </div>

      <div className="flex justify-end mb-4">
        <Button onClick={handleAnalyze} disabled={submitting}
          className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] px-8 py-6 text-base disabled:opacity-50">
          {submitting ? "Analysing your resume…" : "Analyse My Resume →"}
        </Button>
      </div>
      {analyzeProgress.isRunning && (
        <div className="mb-12">
          <ProgressSteps steps={ANALYZE_STEPS} currentStep={analyzeProgress.currentStep} pct={analyzeProgress.pct} />
        </div>
      )}
      {!analyzeProgress.isRunning && <div className="mb-12" />}

      {/* ─── Results ─── */}
      {result && (
        <div ref={resultsRef} className="mb-12">
          <div className="rounded-xl border border-border">
            <div className="p-6 bg-secondary/40 font-mono text-[10px] tracking-[0.25em] border-b border-border">ATS SIMULATION RESULTS</div>

            {/* Scores */}
            <div className="grid grid-cols-1 md:grid-cols-3 border-b border-border divide-y md:divide-y-0 md:divide-x divide-border">
              <div className="p-8 flex items-center justify-center">
                <ScoreRing value={result.ats_score} label="ATS SCORE" />
              </div>
              <div className="p-8 flex items-center justify-center">
                <ScoreRing value={result.keyword_match_rate} label="KEYWORD MATCH RATE" />
              </div>
              <div className="p-8 flex flex-col items-center justify-center gap-3">
                <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em]">FORMATTING</div>
                {result.formatting_pass ? (
                  <div className="flex flex-col items-center gap-2">
                    <ShieldCheck className="w-12 h-12 text-[#10B981]" />
                    <span className="font-display font-bold text-xl text-[#10B981]">PASS</span>
                    <span className="font-mono text-[10px] text-muted-foreground text-center">ATS-friendly formatting detected</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <ShieldX className="w-12 h-12 text-[#E11D48]" />
                    <span className="font-display font-bold text-xl text-[#E11D48]">FAIL</span>
                    <span className="font-mono text-[10px] text-muted-foreground text-center">{result.formatting_issues?.length || 0} issue(s) found</span>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="px-6 py-5 border-b border-border bg-secondary/20">
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em] mb-2">SUMMARY</div>
              <p className="text-sm leading-relaxed">{result.summary}</p>
            </div>

            {/* Formatting issues */}
            {!result.formatting_pass && result.formatting_issues?.length > 0 && (
              <div className="px-6 py-5 border-b border-border bg-[#E11D48]/5">
                <div className="font-mono text-[10px] text-[#E11D48] tracking-[0.25em] mb-3">FORMATTING ISSUES</div>
                <ul className="space-y-2">
                  {result.formatting_issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#E11D48]">
                      <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Keywords */}
            <div className="grid grid-cols-1 md:grid-cols-2 border-b border-border divide-y md:divide-y-0 md:divide-x divide-border">
              <div className="p-6">
                <div className="font-mono text-[10px] text-[#10B981] tracking-[0.25em] mb-3">
                  KEYWORDS FOUND <span className="text-muted-foreground">({result.keywords_found?.length || 0})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(result.keywords_found || []).map((k, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]">
                      <CheckCircle2 className="w-3 h-3" />{k}
                    </span>
                  ))}
                  {!result.keywords_found?.length && <span className="text-sm text-muted-foreground">None detected</span>}
                </div>
              </div>
              <div className="p-6">
                <div className="font-mono text-[10px] text-[#E11D48] tracking-[0.25em] mb-3">
                  KEYWORDS MISSING <span className="text-muted-foreground">({result.keywords_missing?.length || 0})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(result.keywords_missing || []).map((k, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono border border-[#E11D48]/40 bg-[#E11D48]/10 text-[#E11D48]">
                      <XCircle className="w-3 h-3" />{k}
                    </span>
                  ))}
                  {!result.keywords_missing?.length && <span className="text-sm text-muted-foreground">None — great coverage!</span>}
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="p-6 border-b border-border">
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em] mb-3">ACTIONABLE TIPS TO IMPROVE ATS SCORE</div>
              <ol className="space-y-2">
                {(result.actionable_tips || []).map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="font-mono text-[10px] text-muted-foreground mt-1 w-4 flex-shrink-0">{i + 1}.</span>{t}
                  </li>
                ))}
              </ol>
            </div>

            {/* Optimise CTA / Optimised result */}
            <div className="p-6">
              {!showOptimized ? (
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <div className="font-display font-bold text-lg">Want a higher ATS score?</div>
                      <p className="text-sm text-muted-foreground mt-1">AI rewrites your resume with missing keywords, standard headers, and ATS-safe formatting.</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Button onClick={handleOptimize} disabled={optimizing} className="rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9]">
                        <Sparkles className="w-4 h-4 mr-2" />
                        {optimizing ? "Optimising for ATS…" : "Optimise for ATS →"}
                      </Button>
                      {isFree && <span className="font-mono text-[10px] text-muted-foreground">Free plan: 1 optimization / month</span>}
                    </div>
                  </div>
                  {optimizeProgress.isRunning && (
                    <ProgressSteps steps={OPTIMIZE_STEPS} currentStep={optimizeProgress.currentStep} pct={optimizeProgress.pct} />
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div className="font-display font-bold text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#7C3AED]" /> ATS-Optimised Resume
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" className="rounded-xl h-8 text-xs"
                        onClick={() => { navigator.clipboard.writeText(optimized.optimized_resume); toast.success("Copied to clipboard"); }}>
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                      <Button variant="outline" className="rounded-xl h-8 text-xs" onClick={() => setShowOptimized(false)}>Hide</Button>
                    </div>
                  </div>

                  {/* Before / After ATS score */}
                  {optimized?.post_ats_score != null && (
                    <div className="flex items-center gap-6 mb-4 rounded-xl border border-border p-4 bg-white">
                      <div className="text-center">
                        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">BEFORE</div>
                        <div className="font-display text-3xl font-black" style={{ color: atsColor(result?.ats_score || 0) }}>
                          {result?.ats_score ?? "—"}
                        </div>
                      </div>
                      <div className="text-2xl text-muted-foreground font-light">→</div>
                      <div className="text-center">
                        <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-1">AFTER</div>
                        <div className="font-display text-3xl font-black" style={{ color: atsColor(optimized.post_ats_score) }}>
                          {optimized.post_ats_score}
                        </div>
                      </div>
                      <div className="ml-2">
                        <span className="font-mono text-xs text-[#10B981] font-bold">
                          {optimized.post_ats_score - (result?.ats_score || 0) > 0
                            ? `+${optimized.post_ats_score - (result?.ats_score || 0)} pts`
                            : `${optimized.post_ats_score - (result?.ats_score || 0)} pts`}
                        </span>
                        <div className="font-mono text-[10px] text-muted-foreground">estimated improvement</div>
                      </div>
                    </div>
                  )}

                  {/* Changes made with confidence tiers */}
                  {optimized?.changes_made?.length > 0 && (
                    <div className="mb-4 rounded-xl border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-4">
                      <div className="font-mono text-[10px] text-[#7C3AED] tracking-[0.25em] mb-3">ATS IMPROVEMENTS MADE</div>
                      <ul className="space-y-2">
                        {optimized.changes_made.map((c, i) => {
                          const isObj = typeof c === "object";
                          const text = isObj ? c.text : c;
                          const confidence = isObj ? c.confidence : null;
                          return (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <span className="text-[#7C3AED] mt-0.5 flex-shrink-0">→</span>
                              <span className="flex-1">{text}</span>
                              {confidence === "HIGH_CONFIDENCE" && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-mono font-bold bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30">
                                  VERIFIED
                                </span>
                              )}
                              {confidence === "CONSIDER" && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-mono font-bold bg-[#F59E0B]/10 text-[#92400E] border border-[#F59E0B]/40">
                                  REVIEW
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      <div className="mt-3 pt-3 border-t border-[#7C3AED]/20 flex gap-4 text-[10px] font-mono text-muted-foreground">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#10B981]/30 border border-[#10B981]/50 inline-block" /> VERIFIED — directly supported by your resume</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#F59E0B]/30 border border-[#F59E0B]/50 inline-block" /> REVIEW — verify before using</span>
                      </div>
                    </div>
                  )}

                  {/* Skill gaps */}
                  {optimized?.skill_gaps?.length > 0 && (
                    <div className="mb-4 border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4">
                      <div className="font-mono text-[10px] text-[#92400E] tracking-[0.25em] mb-3">SKILL GAPS TO ADDRESS</div>
                      <p className="text-xs text-muted-foreground mb-3">These JD requirements could not be honestly added to your resume. Consider acquiring them:</p>
                      <ul className="space-y-1.5">
                        {optimized.skill_gaps.map((gap, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                            {gap}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Template picker — live preview + PDF download */}
                  <div className="mb-4">
                    {optimized?.resume_structured ? (
                      <ResumeTemplatePicker
                          data={optimized.resume_structured}
                          candidateName={optimized.resume_structured?.contact?.name}
                          editable
                          analysisId={result?.analysis_id}
                          savedResumeId={
                            optimizedSavedToId && optimizedSavedToId !== "new"
                              ? optimizedSavedToId
                              : simulationSavedId
                          }
                        />
                    ) : (
                      <pre className="rounded-xl border border-border p-4 bg-secondary/30 text-sm whitespace-pre-wrap font-mono leading-relaxed overflow-auto max-h-[500px]">
                        {optimized?.optimized_resume}
                      </pre>
                    )}
                  </div>

                  {/* Save optimised resume */}
                  {!optimizedSavedToId && (
                    <SaveOptimizedPanel
                      optimized={optimized}
                      savedResumes={savedResumes}
                      resumesAtLimit={resumesAtLimit}
                      savingOptimized={savingOptimized}
                      onSaveTo={handleSaveOptimizedTo}
                      onSaveBoth={handleSaveBothAsNew}
                    />
                  )}
                  {optimizedSavedToId && (
                    <div className="flex items-center gap-2 text-sm text-[#10B981] mb-3">
                      <CheckCircle2 className="w-4 h-4" />
                      {optimizedSavedToId === "new"
                        ? "Saved as new resume with both versions"
                        : <>Saved to <strong>{savedResumes.find(r => r.resume_id === optimizedSavedToId)?.name}</strong></>}
                    </div>
                  )}

                  <Button variant="outline" className="rounded-xl text-xs" onClick={() => { setOptimized(null); setShowOptimized(false); }}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Rewrite again
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Resume Hub link ─── */}
      <section className="mb-12">
        <div className="rounded-xl border border-border p-6 flex items-center justify-between gap-4 bg-white">
          <div>
            <div className="font-display font-bold text-lg">My Resumes</div>
            <p className="text-sm text-muted-foreground mt-1">
              You have <strong>{savedResumes.length}</strong> saved resume{savedResumes.length !== 1 ? "s" : ""}. Upload, rename, download, and manage them in the Resume Hub.
            </p>
          </div>
          <Link to="/resumes">
            <Button variant="outline" className="rounded-xl shrink-0">
              Manage resumes →
            </Button>
          </Link>
        </div>
      </section>

      {/* ─── Saved Analyses ─── */}
      <section>
        <h2 className="font-display font-bold text-2xl mb-4">
          Saved analyses
          <span className="font-mono text-sm font-normal text-muted-foreground ml-3">{analyses.length} / {MAX_ANALYSES}</span>
        </h2>

        {loadingHistory ? (
          <div className="font-mono text-sm text-muted-foreground">Loading…</div>
        ) : analyses.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
            No saved analyses yet. Analyse your first resume above.
          </div>
        ) : (
          <div className="border-l border-t border-border">
            <div className="grid grid-cols-12 border-r border-b border-border bg-secondary/40 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <div className="col-span-1 p-3 border-r border-border">ATS</div>
              <div className="col-span-1 p-3 border-r border-border">KW%</div>
              <div className="col-span-1 p-3 border-r border-border">Format</div>
              <div className="col-span-6 p-3 border-r border-border">Job Description</div>
              <div className="col-span-1 p-3 border-r border-border">Date</div>
              <div className="col-span-2 p-3" />
            </div>
            {analyses.map((a) => (
              <div key={a.analysis_id} className={`grid grid-cols-12 border-r border-b border-border items-center ${result?.analysis_id === a.analysis_id ? "bg-[#0025D8]/5" : ""}`}>
                <div className="col-span-1 p-4 border-r border-border">
                  <span className="font-display font-bold text-lg" style={{ color: atsColor(a.ats_score || 0) }}>{a.ats_score ?? "—"}</span>
                </div>
                <div className="col-span-1 p-4 border-r border-border">
                  <span className="font-mono text-sm" style={{ color: atsColor(a.keyword_match_rate || 0) }}>{a.keyword_match_rate ?? "—"}%</span>
                </div>
                <div className="col-span-1 p-4 border-r border-border">
                  {a.formatting_pass
                    ? <Badge variant="outline" className="rounded-xl text-[10px] border-[#10B981]/40 text-[#10B981]">PASS</Badge>
                    : <Badge variant="outline" className="rounded-xl text-[10px] border-[#E11D48]/40 text-[#E11D48]">FAIL</Badge>}
                </div>
                <div className="col-span-6 p-4 border-r border-border">
                  <div className="text-sm truncate text-muted-foreground">{a.jd_snippet}</div>
                </div>
                <div className="col-span-1 p-4 border-r border-border font-mono text-xs text-muted-foreground">
                  {new Date(a.created_at).toLocaleDateString()}
                </div>
                <div className="col-span-2 p-4 flex gap-2 items-center">
                  <button onClick={() => loadPastResult(a)} className="text-[#0025D8] text-sm font-semibold hover:underline font-mono">→</button>
                  <Button variant="outline" size="icon" onClick={() => handleDeleteAnalysis(a.analysis_id)}
                    className="rounded-xl h-8 w-8 text-[#E11D48] hover:bg-[#E11D48] hover:text-white">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="ats_optimize" />
    </main>
  );
}
